/**
 * Getting Started Component
 * =========================
 * Role-based onboarding checklist for new tenants.
 *
 * Three paths based on WelcomeModal selection (stored in localStorage gc_user_role):
 * - Developer:  API Key → Verify Yourself → See Results → Integrate SDK
 * - Compliance: Verify Yourself → Explore Dashboard → Invite Team
 * - Exploring:  Verify Yourself → See Results
 *
 * Falls back to Developer path if no role is set.
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Key,
  Play,
  Rocket,
  Check,
  ChevronRight,
  Sparkles,
  Eye,
  Code2,
  Users,
  BarChart3,
} from 'lucide-react';
import { useApiKeys } from '../hooks';
import { useTeamMembers } from '../hooks/useSettings';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { cn } from '../lib/utils';

// Step definitions per role
function getDeveloperSteps({ hasApiKey, hasVerified, hasViewedResults }) {
  return [
    {
      id: 'apikey',
      title: 'Your API Key',
      description: 'Your live and test API keys were created automatically. View them in Integrations.',
      action: hasApiKey ? 'View Keys' : 'View Keys',
      completed: hasApiKey,
      route: '/integrations',
      icon: Key,
    },
    {
      id: 'verify',
      title: 'Verify Yourself',
      description: 'Experience the exact verification flow your users will see. Takes about 2 minutes.',
      action: 'Start Verification',
      completed: hasVerified,
      route: '/sdk/demo',
      icon: Play,
    },
    {
      id: 'results',
      title: 'See Your Results',
      description: 'View your applicant profile with AI risk scoring, document analysis, and biometrics.',
      action: 'View Results',
      completed: hasViewedResults,
      route: '/applicants',
      icon: Eye,
    },
    {
      id: 'integrate',
      title: 'Integrate the SDK',
      description: 'Add verification to your app with our React SDK or REST API. Quickstart takes 5 minutes.',
      action: 'View Quickstart',
      completed: localStorage.getItem('gc_onboarding_integrated') === 'true',
      route: '/dev-workbench',
      icon: Code2,
    },
  ];
}

function getComplianceSteps({ hasVerified, hasViewedApplicant, hasTeamMembers }) {
  return [
    {
      id: 'verify',
      title: 'Verify Yourself',
      description: 'See exactly what your applicants experience. Takes about 2 minutes.',
      action: 'Start Verification',
      completed: hasVerified,
      route: '/sdk/demo',
      icon: Play,
    },
    {
      id: 'dashboard',
      title: 'Explore the Dashboard',
      description: 'Review your applicant profile with AI risk narrative, documents, and screening results.',
      action: 'View Dashboard',
      completed: hasViewedApplicant,
      route: '/applicants',
      icon: BarChart3,
    },
    {
      id: 'team',
      title: 'Invite Your Team',
      description: 'Add reviewers and analysts to help manage your compliance operations.',
      action: 'Invite Members',
      completed: hasTeamMembers,
      route: '/settings/team',
      icon: Users,
    },
  ];
}

function getExploringSteps({ hasVerified, hasViewedResults }) {
  return [
    {
      id: 'verify',
      title: 'Verify Yourself',
      description: 'Experience the full verification flow. See ID capture, selfie matching, and liveness checks.',
      action: 'Start Verification',
      completed: hasVerified,
      route: '/sdk/demo',
      icon: Play,
    },
    {
      id: 'results',
      title: 'See Your Results',
      description: 'View your applicant profile with AI-powered risk assessment and document analysis.',
      action: 'View Results',
      completed: hasViewedResults,
      route: '/applicants',
      icon: Eye,
    },
  ];
}

// Footer copy per role
const FOOTER_COPY = {
  developer: 'Your API key is ready. Verify yourself, then integrate the SDK into your app in minutes.',
  compliance: 'See the platform from your applicants\' perspective, then invite your team to start reviewing.',
  exploring: 'Try a real verification to see how AI-powered identity checks work.',
};

export default function GettingStarted({ onDismiss }) {
  const navigate = useNavigate();
  const { data: apiKeys } = useApiKeys();
  const { data: teamData } = useTeamMembers();

  // Get user role from welcome modal selection
  const userRole = localStorage.getItem('gc_user_role') || 'developer';

  // Check completion states
  const hasApiKey = (apiKeys?.items?.length ?? 0) > 0;
  const hasVerified = localStorage.getItem('gc_onboarding_tested') === 'true';
  const hasViewedResults = localStorage.getItem('gc_onboarding_viewed') === 'true';
  const hasTeamMembers = (teamData?.length ?? 0) > 1;

  const steps = useMemo(() => {
    const state = { hasApiKey, hasVerified, hasViewedResults, hasViewedApplicant: hasViewedResults, hasTeamMembers };
    switch (userRole) {
      case 'compliance':
        return getComplianceSteps(state);
      case 'exploring':
        return getExploringSteps(state);
      default:
        return getDeveloperSteps(state);
    }
  }, [userRole, hasApiKey, hasVerified, hasViewedResults, hasTeamMembers]);

  const completedSteps = steps.filter((s) => s.completed).length;
  const totalSteps = steps.length;
  const allComplete = completedSteps === totalSteps;

  const handleStepClick = (step) => {
    // Mark verify step as tested when clicking it
    if (step.id === 'verify') {
      localStorage.setItem('gc_onboarding_tested', 'true');
    }
    if (step.id === 'results' || step.id === 'dashboard') {
      localStorage.setItem('gc_onboarding_viewed', 'true');
    }
    if (step.id === 'integrate') {
      localStorage.setItem('gc_onboarding_integrated', 'true');
    }
    navigate(step.route);
  };

  if (allComplete) {
    return (
      <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20 mb-6">
        <div className="flex items-center gap-5 p-6">
          <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center text-green-500 shrink-0">
            <Rocket size={32} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-grey-900 dark:text-grey-50 m-0 mb-1">
              You're all set!
            </h3>
            <p className="text-sm text-grey-500 dark:text-grey-400 m-0">
              {userRole === 'developer'
                ? 'Your verification flow is ready. Integrate the SDK into your app to start verifying users.'
                : userRole === 'compliance'
                  ? 'You\'ve seen the platform in action. Your team is ready to start reviewing applicants.'
                  : 'You\'ve experienced the full verification flow. Upgrade to unlock more features.'}
            </p>
          </div>
          <Button variant="secondary" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border-teal-500/20 mb-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5 text-teal-500">
          <Sparkles size={20} />
          <h2 className="text-lg font-semibold text-grey-900 dark:text-grey-50 m-0">
            Getting Started
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-grey-500 dark:text-grey-400">
            {completedSteps} of {totalSteps} complete
          </span>
          <Progress value={(completedSteps / totalSteps) * 100} className="w-[100px]" />
        </div>
      </div>

      {/* Steps */}
      <div className="flex flex-col gap-3">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={cn(
              'flex items-center gap-4 p-4 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-800 rounded-xl cursor-pointer transition-all duration-150',
              'hover:border-teal-500 hover:translate-x-1',
              step.completed && 'opacity-70 hover:opacity-100'
            )}
            onClick={() => handleStepClick(step)}
          >
            {/* Step number */}
            <div
              className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold text-white shrink-0',
                step.completed ? 'bg-green-500' : 'bg-teal-500'
              )}
            >
              {step.completed ? <Check size={16} /> : index + 1}
            </div>

            {/* Icon */}
            <div className="w-12 h-12 bg-grey-100 dark:bg-grey-800 rounded-lg flex items-center justify-center text-grey-500 dark:text-grey-400 shrink-0">
              <step.icon size={24} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-[15px] font-semibold text-grey-900 dark:text-grey-50 m-0 mb-1">
                {step.title}
              </h3>
              <p className="text-sm text-grey-500 dark:text-grey-400 m-0 leading-relaxed">
                {step.description}
              </p>
            </div>

            {/* Action button */}
            <div
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium shrink-0 transition-colors',
                step.completed
                  ? 'bg-grey-100 dark:bg-grey-800 text-grey-500'
                  : 'bg-teal-500 text-white hover:bg-teal-600'
              )}
            >
              <span>{step.completed ? 'Done' : step.action}</span>
              <ChevronRight size={16} />
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-5 pt-4 border-t border-grey-200 dark:border-grey-800">
        <p className="text-sm text-grey-500 dark:text-grey-400 m-0 leading-relaxed">
          {FOOTER_COPY[userRole] || FOOTER_COPY.developer}
        </p>
      </div>
    </Card>
  );
}
